import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  Text,
  Checkbox,
  Box,
  Divider,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  RadioGroup,
  Radio,
  Stack,
} from "components-lib";

const DoctorModal = ({ isOpen, onClose, onSubmit, initialData }) => {
  const [formData, setFormData] = useState(
    initialData || {
      prescriptionType: "digital", // 'digital' or 'letterhead'
      margins: {
        top: 20,
        right: 20,
        bottom: 20,
        left: 20,
      },
      letterhead: {
        enabled: false,
        margins: {
          top: 20,
          right: 20,
          bottom: 20,
          left: 20,
        },
      },
      layoutPreferences: {
        showHeader: true,
        showPatientDetails: true,
        showparameters: true,
        showAllergies: true,
        showComplaints: true,
        showDiagnosis: true,
        showMedicines: true,
        showInvestigations: true,
        showPastMedications: true,
        showClinicalFindings: true, // added Clinical Findings toggle
        showPastHistory: true, // added Past History toggle
        showAdvice: true,
        showReferredTo: true,
        showFollowUp: true,
        showFooter: true,
        showQRCode: false,
        showUPI: true,
      },
    }
  );

  const handlePrescriptionTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      prescriptionType: value,
      letterhead: {
        ...prev.letterhead,
        enabled: value === "letterhead",
      },
    }));
  };

  const handleLayoutPreferenceChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      layoutPreferences: {
        ...prev.layoutPreferences,
        [name]: checked,
      },
    }));
  };

  const handleMarginChange = (direction, value) => {
    setFormData((prev) => ({
      ...prev,
      margins: {
        ...prev.margins,
        [direction]: value,
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Print Preferences</ModalHeader>
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <VStack spacing={4}>
              <Box w="100%">
                {/* Prescription Type Selection */}
                <Box mb={6}>
                  <Text fontWeight="medium" mb={3}>
                    Prescription Type
                  </Text>
                  <RadioGroup
                    value={formData.prescriptionType}
                    onChange={handlePrescriptionTypeChange}
                  >
                    <Stack direction="row" spacing={6}>
                      <Radio value="digital">Digital Prescription</Radio>
                      <Radio value="letterhead">Letterhead</Radio>
                    </Stack>
                  </RadioGroup>
                </Box>

                <Divider my={4} />

                {/* Margin Settings */}
                <Box mb={6}>
                  <Text fontWeight="medium" mb={3}>
                    {formData.prescriptionType === "letterhead"
                      ? "Content Margins (px)"
                      : "Page Margins (px)"}
                  </Text>
                  <Text fontSize="sm" color="gray.600" mb={3}>
                    Adjust margins for content positioning from all four sides
                  </Text>
                  <HStack spacing={4}>
                    <FormControl>
                      <FormLabel>Top</FormLabel>
                      <NumberInput
                        value={formData.margins.top}
                        onChange={(value) => handleMarginChange("top", value)}
                        min={0}
                        max={200}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Right</FormLabel>
                      <NumberInput
                        value={formData.margins.right}
                        onChange={(value) => handleMarginChange("right", value)}
                        min={0}
                        max={200}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Bottom</FormLabel>
                      <NumberInput
                        value={formData.margins.bottom}
                        onChange={(value) =>
                          handleMarginChange("bottom", value)
                        }
                        min={0}
                        max={200}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Left</FormLabel>
                      <NumberInput
                        value={formData.margins.left}
                        onChange={(value) => handleMarginChange("left", value)}
                        min={0}
                        max={200}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  </HStack>
                </Box>

                <Divider my={4} />

                {/* Layout Preferences */}
                <Box>
                  <Text fontWeight="medium" mb={3}>
                    Layout Options
                  </Text>
                  <VStack align="start" spacing={3}>
                    <Checkbox
                      name="showPatientDetails"
                      isChecked={formData.layoutPreferences.showPatientDetails}
                      onChange={handleLayoutPreferenceChange}
                    >
                      Patient Details
                    </Checkbox>

                    <Checkbox
                      name="showClinicalFindings"
                      isChecked={
                        !!formData.layoutPreferences.showClinicalFindings
                      }
                      onChange={handleLayoutPreferenceChange}
                    >
                      Clinical Findings
                    </Checkbox>

                    <Checkbox
                      name="showparameters"
                      isChecked={formData.layoutPreferences.showparameters}
                      onChange={handleLayoutPreferenceChange}
                    >
                      parameters
                    </Checkbox>
                    <Checkbox
                      name="showAllergies"
                      isChecked={formData.layoutPreferences.showAllergies}
                      onChange={handleLayoutPreferenceChange}
                    >
                      Allergies
                    </Checkbox>
                    <Checkbox
                      name="showComplaints"
                      isChecked={formData.layoutPreferences.showComplaints}
                      onChange={handleLayoutPreferenceChange}
                    >
                      Complaints
                    </Checkbox>
                    <Checkbox
                      name="showDiagnosis"
                      isChecked={formData.layoutPreferences.showDiagnosis}
                      onChange={handleLayoutPreferenceChange}
                    >
                      Diagnosis
                    </Checkbox>
                    <Checkbox
                      name="showMedicines"
                      isChecked={formData.layoutPreferences.showMedicines}
                      onChange={handleLayoutPreferenceChange}
                    >
                      Medicines
                    </Checkbox>
                    <Checkbox
                      name="showInvestigations"
                      isChecked={
                        !!formData.layoutPreferences.showInvestigations
                      }
                      onChange={handleLayoutPreferenceChange}
                    >
                      Investigations
                    </Checkbox>
                    <Checkbox
                      name="showPastMedications"
                      isChecked={
                        !!formData.layoutPreferences.showPastMedications
                      }
                      onChange={handleLayoutPreferenceChange}
                    >
                      Past Medications
                    </Checkbox>
                    <Checkbox
                      name="showPastHistory"
                      isChecked={!!formData.layoutPreferences.showPastHistory}
                      onChange={handleLayoutPreferenceChange}
                    >
                      Past History
                    </Checkbox>
                    <Checkbox
                      name="showAdvice"
                      isChecked={formData.layoutPreferences.showAdvice}
                      onChange={handleLayoutPreferenceChange}
                    >
                      Advice
                    </Checkbox>
                    <Checkbox
                      name="showReferredTo"
                      isChecked={formData.layoutPreferences.showReferredTo}
                      onChange={handleLayoutPreferenceChange}
                    >
                      Referred To
                    </Checkbox>
                    <Checkbox
                      name="showFollowUp"
                      isChecked={!!formData.layoutPreferences.showFollowUp}
                      onChange={handleLayoutPreferenceChange}
                    >
                      Follow up
                    </Checkbox>
                    <Checkbox
                      name="showFooter"
                      isChecked={formData.layoutPreferences.showFooter}
                      onChange={handleLayoutPreferenceChange}
                    >
                      Footer (Signature & Date)
                    </Checkbox>
                    <Checkbox
                      name="showQRCode"
                      isChecked={formData.layoutPreferences.showQRCode}
                      onChange={handleLayoutPreferenceChange}
                    >
                      QR Code
                    </Checkbox>
                    <Checkbox
                      name="showUPI"
                      isChecked={formData.layoutPreferences.showUPI}
                      onChange={handleLayoutPreferenceChange}
                    >
                      UPI Payment Details
                    </Checkbox>
                  </VStack>
                </Box>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} type="submit">
              Save Preferences
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default DoctorModal;
